import { createApp, h, ref, Ref } from "vue"
import { createPinia } from "pinia"
import router from "./router/router"
import App from "./App.vue"
import { createI18n } from "vue-i18n"
// import { registerSW } from "virtual:pwa-register"
import PrimeVue from "primevue/config"
import Aura from "@primevue/themes/aura"
import VueSplide from "@splidejs/vue-splide"
import PulseLoader from "vue-spinner/src/PulseLoader.vue"
import vueSpatialNavigation from "vue-spatial-nav"
import "@splidejs/vue-splide/css"
import Dropdown from "primevue/dropdown"
import "primeicons/primeicons.css"
import { isNativeDevice } from "./helper"
// import piniaPluginPersistedstate from "pinia-plugin-persistedstate"
import { languageData } from "../src/i18n/i18n"
import Tooltip from "primevue/tooltip"
import { App as CapacitorApp } from "@capacitor/app"
import { CapacitorSwipeBackPlugin } from "@aliadamovich/capacitor-swipe-back-plugin"
import { toast } from "vue-sonner"
import { Capacitor } from "@capacitor/core"
import { SplashScreen } from "@capacitor/splash-screen"
import { Device } from "@capacitor/device"

if (Capacitor.getPlatform() === "ios") {
	CapacitorSwipeBackPlugin.enable()
}

// Stores a global error message to be displayed on the ErrorPage whevener any error is thrown in the app.
export const globalErrorMessage: Ref<string | null> = ref(null)

/**
 * Gets the device language on native platforms, falls back to "el" on web
 */
async function getDeviceLanguage(): Promise<string> {
	if (Capacitor.isNativePlatform()) {
		try {
			const languageCode = await Device.getLanguageCode()
			// Extract the language code (e.g., "en-US" -> "en", "el-GR" -> "el")
			return languageCode.value.split("-")[0] || "el"
		} catch (error) {
			console.error("Error getting device language:", error)
			return "el"
		}
	}
	return "el"
}

async function initializeApp() {
	const app = createApp(App)

	const pinia = createPinia()
	// pinia.use(piniaPluginPersistedstate)

	// Get device language first
	const deviceLanguage = await getDeviceLanguage()

	const urlParams = new URLSearchParams(window.location.search)
	const langFromUrl = urlParams.get("lang")
	const defaultLanguage = localStorage.getItem("preferred_langcode") || langFromUrl || deviceLanguage
	const i18n = createI18n({
		legacy: false,
		locale: defaultLanguage,
		fallbackLocale: defaultLanguage === "el" ? "en" : "el",
		messages: languageData,
	})

	const globalConfig = {
		straightOnly: false,
		straightOverlapThreshold: 0.5,
		rememberSource: false,
		disabled: false,
		defaultElement: ".app-logo",
		enterTo: ".app-logo",
		leaveFor: undefined,
		restrict: "self-first",
		tabIndexIgnoreList: "a, input, select, textarea, button, [tabindex]:not([tabindex='-1'])",
		navigableFilter: undefined,
		scrollOptions: {
			behavior: "smooth",
		},
	}

	app.use(router)
	app.use(pinia)
	if (isNativeDevice) {
		app.use(vueSpatialNavigation, globalConfig)
	}

	app.use(VueSplide)
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
		},
	})
	app.use(i18n)
	app.component("Dropdown", Dropdown)
	if (!isNativeDevice) {
		app.directive("tooltip", Tooltip)
		app.component("PulseLoader", PulseLoader)
	}

	/**
	 * Handles global errors by setting the globalErrorMessage and navigating to the ErrorPage.
	 * @param err
	 */
	app.config.errorHandler = (err: unknown): void => {
		globalErrorMessage.value = (err as Error).message || "An unexpected error occurred"
		console.error("Global error handler:", err)
		// alert("Global error: " + globalErrorMessage.value)
		// router.push({ name: "ErrorPage" })
		if ((err as any).render) {
			toast.error(globalErrorMessage.value)
		}
	}

	app.mount("#app")

	// Handle native back button (Android) and swipe gestures
	CapacitorApp.addListener("backButton", ({ canGoBack }) => {
		if (canGoBack) {
			router.back()
		} else {
			CapacitorApp.exitApp()
		}
	})
}

// Initialize the app
initializeApp()
